<template>
	<div v-if="!preloaded" class="fixed inset-0 flex flex-col gap-6 justify-center items-center text-center text-xs">
		<p v-if="!preloadingFailed">{{ t('we_put_together_the_available_options_for_you') }}</p>
		<LoadingIndicator v-if="!preloadingFailed" />

		<Transition name="fade">
			<div v-if="preloadingFailed" class="mt-10">
				<p v-if="errorMessage" class="text-red-600 font-bold mb-4 text-sm max-w-lg">{{ errorMessage }}</p>
				<p>{{ t('something_went_wrong_while_retrieving_your_data') }}</p>
				<p>{{ t('when_resetting_previously_entered_data_will_be_lost') }}</p>
				<p v-if="$flowstore.id" class="my-4">ID: {{ $flowstore.id }}</p>
				<nav class="flex flex-wrap justify-center gap-2 mt-6">
					<ActionButton @click="reload">{{ t('reload') }}</ActionButton>
					<ActionButton variant="light" @click="reinitialize">{{ t('reset') }}</ActionButton>
				</nav>
			</div>
		</Transition>
	</div>

	<template v-if="preloaded">
		<AppHeader />

		<main :class="['container-inset min-h-[calc(100vh-8rem)] py-12 flex', stepClass]">
			<div class="container flex flex-1 justify-between gap-10 xl:gap-28">
				<div class="flex-grow min-w-0 z-10">
					<LoadingOverlay v-if="$loadingstore.validating_step || $loadingstore.validating_booking" fixed />
					<div class="isolate">
						<DataLayerTracking>
							<RouterView v-slot="{ Component: RouteComponent }">
								<Transition v-if="RouteComponent" name="page" mode="out-in">
									<Component :is="RouteComponent" v-if="RouteComponent" />
								</Transition>
							</RouterView>
						</DataLayerTracking>
					</div>
				</div>
				<aside class="flex-none hidden lg:block w-full max-w-md xl:max-w-lg">
					<ReceiptSidebar />
				</aside>
			</div>
		</main>

		<AppFooter v-if="!readOnlyMode" />
	</template>

	<ResumeReservationModal
		ref="resumeModal"
		:notice="priceNotice"
		:reasons="unavailableReasons"
		@resume="resumeReservation"
		@restart="restartReservation"
	/>
	<ReservationChangedModal ref="changedModal" :reasons="changeReasons" @restart="restartAfterChange" />
</template>

<script lang="ts" setup>
import { isAxiosError } from 'axios';
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import {
  isDifferentReservationRequest,
  parseProductId,
  shouldStartNewFlow,
  withoutReservationParams,
} from '@/composables/useFlowStart';
import { useCustomerStore } from '@/stores/useCustomerStore';
import { hasPriceChanged } from '@/stores/resumableSession';
import { isValidationError } from '@/api/errors';
import { useResumableReservation } from '@/composables/useResumableReservation';
import { useFlowStore, type CheckoutOptions } from '@/stores/useFlowStore';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { useValidationStore } from '@/stores/useValidationStore';
import { useRoute, useRouter } from 'vue-router';
import ActionButton from '@/components/ActionButton.vue';
import AppFooter from '@/partials/AppFooter.vue';
import AppHeader from '@/partials/AppHeader.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import ReceiptSidebar from '@/components/ReceiptSidebar.vue';
import ReservationChangedModal from '@/components/modals/ReservationChangedModal.vue';
import ResumeReservationModal from '@/components/modals/ResumeReservationModal.vue';
import qs from 'qs';
import DataLayerTracking from "@/partials/DataLayerTracking.vue";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const flowStore = useFlowStore();
const navStore = useNavigationStore();
const customerStore = useCustomerStore();
const validationStore = useValidationStore();
const route = useRoute();
const router = useRouter();
const preloaded = ref(false);
const preloadingFailed = ref(false);
const stepClass = ref('');
const errorMessage = ref();
const readOnlyMode = computed(() => route.query.ro === 'true');
const { isResumable, lastKnownTotal, rememberTotal } = useResumableReservation();
const askToResume = ref(false);
const unavailableReasons = ref<string[]>([]);
const priceNotice = ref('');

// Only for a visit without a resume question: that question carries both the price notice and
// the reasons itself, so a guest never gets two modals in a row.
const changeReasons = computed(() => [
  ...unavailableReasons.value,
  ...(priceNotice.value ? [priceNotice.value] : []),
]);
const restartOptions = ref<CheckoutOptions>();
const resumeModal = ref<InstanceType<typeof ResumeReservationModal>>();
const changedModal = ref<InstanceType<typeof ReservationChangedModal>>();

// Every change the guest makes ends up here, whichever endpoint applied it, so this always
// holds the total as last shown to them. A difference on the next visit is therefore the
// server's doing, not theirs. A read-only preview is nobody's reservation, so it records
// nothing: its total would otherwise become the baseline for the guest's real visit.
watch(
  () => flowStore.checkout.meta?.total_price,
  (total) => {
    if (!readOnlyMode.value) rememberTotal(total);
  },
);

watch(route, r => {
  // #2617: We want to apply a class to the main container
  stepClass.value = 'step-' + r.name;

  // query watcher needed for CheckoutTypeCard
  if (r.query.checkout_type && r.query.checkout_type !== '') reinitialize();
});

function collectUnavailableReasons(): string[] {
  // validateStep clears the store first, so whatever is left came from the reservation step.
  return validationStore.messages.length
    ? validationStore.messages
    : [t('one_or_more_choices_are_no_longer_available')];
}

async function doPreload(restartWith?: CheckoutOptions) {
  errorMessage.value = null;
  preloaded.value = false;
  preloadingFailed.value = false;

  let shouldRedirectToFirstStep = false;

  // Snapshot before loading: the watcher below overwrites the stored total as soon as the
  // new one arrives, so this is the last total the guest actually saw.
  const previousTotal = lastKnownTotal.value;

  /**
   * Starting the flow with a new product.
   */

  const query = qs.parse(window.location.search, {ignoreQueryPrefix: true});
  const urlSearchParams = new URLSearchParams(window.location.search);
  const rawReadOnlyParam = urlSearchParams.get('ro');
  const isReadOnlyQuery = rawReadOnlyParam === 'true';
  const accessToken = query.access_token;
  if (accessToken) {
    await customerStore.loginWithAccessToken(accessToken);
    shouldRedirectToFirstStep = true;
  }

  // 'cluster' is used for backward compatibility
  const startProductId = parseProductId(query.cluster || query.product);

  const qsBathCardId = query.bath_card_id;
  const startBathCardId = qsBathCardId ? parseInt(qsBathCardId, 10) : undefined;

  const qsVoucherCodes = query.voucher_codes || query.voucher_code;
  const startVoucherCodes = qsVoucherCodes ? qsVoucherCodes.split(',') : undefined;

  // for (const value of query.values()) console.log(value);
  let checkoutOptions: CheckoutOptions = restartWith ?? {
    checkout: query.checkout,
    product: startProductId,
    voucher_codes: startVoucherCodes,
    arrival_date: query.arrival_date,
    departure_date: query.departure_date,
    bath_card_id: startBathCardId,
    persons_count: query.persons_count,
    persons: query.persons,
    amount_of_rooms: 1,
    checkout_type: query.checkout_type,
    nights: query.nights,
    ga_origin: query.ga_origin ?? 'Direct',
    ga_linker_code: query._gl,
    read_only: isReadOnlyQuery,
  };

  if (isReadOnlyQuery) {
    checkoutOptions.persons_count = '1';
    checkoutOptions.amount_of_rooms = 1;
  }

  if (!isReadOnlyQuery && query.persons_count) {
    checkoutOptions.amount_of_rooms = (Math.ceil(parseInt(query.persons_count) / 2));
  } else if(!isReadOnlyQuery && query.persons) {
    checkoutOptions.amount_of_rooms = (Math.ceil(parseInt(query.persons.length) / 2));
  }

  // When in readonly mode with an existing flow and no new product specified,
  // reuse the existing flow instead of creating a new one (e.g. on page refresh with ?ro=true)
  const hasExistingReadOnlyFlow = isReadOnlyQuery && flowStore.id && !startProductId;

  if (hasExistingReadOnlyFlow) {
    // Clear checkout options so the existing flow is loaded as-is
    checkoutOptions = {} as CheckoutOptions;
  }

  // When reservation details are passed in the query, we have to reset and start a new checkout flow.
  const wantsNewFlow = !hasExistingReadOnlyFlow && (restartWith !== undefined || shouldStartNewFlow(query));

  const mayResume = wantsNewFlow && !isReadOnlyQuery && !!flowStore.id && isResumable.value;
  askToResume.value = false;

  if (mayResume) {
    // Hold on to what the guest clicked: landing on the remembered step strips the
    // reservation parameters from the url, leaving nothing to start over with.
    restartOptions.value = checkoutOptions;
    checkoutOptions = {} as CheckoutOptions;
  } else if (wantsNewFlow) {
    console.warn('Starting new flow with', checkoutOptions);
    flowStore.resetFlow();
    shouldRedirectToFirstStep = true;
  }

  /**
   * Load the flow store
   */

  try {
    const persistedFlowId = flowStore.id;

    await flowStore.load(checkoutOptions);

    // Reopening the flow for the product the reservation already holds is not a switch,
    // so only ask when the guest is actually after something else.
    askToResume.value =
      mayResume &&
      flowStore.id === persistedFlowId &&
      isDifferentReservationRequest(query, {
        productId: flowStore.checkout.data?.product_id,
        arrivalDate: flowStore.checkout.data?.arrival?.date,
        departureDate: flowStore.checkout.data?.departure?.date,
        personsCount: flowStore.checkout.data?.persons_count,
      });

    const priceChanged =
      flowStore.id === persistedFlowId && hasPriceChanged(previousTotal, flowStore.checkout.meta?.total_price);

    /**
     * Remove unused upsell routes.
     */

    // const upsellRoutes = Object.entries(flowStore.checkout.meta.upsells)
    //   .filter(([key, value]) => {
    //     if (key === 'lunch_and_diner') {
    //       return value.products.length === 0 && flowStore.checkout.meta.upsells.hotel_rooms.products.length === 0
    //     }
    //     return value.products.length === 0; // no products available in upsell step.
    //   })
    //   .map(([name]) => name);

    // removeOptionalRoutes(upsellRoutes);

    // navStore.recomputeAvailableSteps();

    /**
     * Return to the first step (reservation) when:
     *
     * - the persisted flow/id differs from the loaded flow/id.
     * - validation fails on the reservation step.
     */

    let reservationNoLongerValid = false;

    if (navStore.currentStepIndex() > 0 || mayResume) {
      if (flowStore.id !== persistedFlowId) {
        console.warn('New flow/id redirect to first step.');
        shouldRedirectToFirstStep = true;
      } else {
        try {
          await flowStore.validateStep('reservation');
          console.warn('Reservation looks good, continue.');
        } catch (error) {
          // Only a failed validation says something about the reservation; a dropped
          // connection or a server error belongs on the retry screen.
          if (!isValidationError(error)) throw error;

          console.warn('Validation error in reservation step, redirect to first step.');
          shouldRedirectToFirstStep = true;
          reservationNoLongerValid = true;
        }
      }
    }

    unavailableReasons.value = [];
    priceNotice.value = '';

    // Read-only is a preview, not a reservation the guest is working on. An unavailable
    // choice outranks a changed price: what an unbookable product costs is beside the point.
    if (!isReadOnlyQuery) {
      if (reservationNoLongerValid) {
        unavailableReasons.value = collectUnavailableReasons();
      } else if (priceChanged) {
        priceNotice.value = t('the_price_of_your_reservation_has_changed', {
          previous: previousTotal,
          current: flowStore.checkout.meta?.total_price,
        });
      }
    }

    if (shouldRedirectToFirstStep) {
      await navStore.gotoFirstStep();
    } else {
      await navStore.gotoRememberedPath();
    }

    if (isReadOnlyQuery) {
      const productInQuery = typeof query.product === 'string' ? query.product : undefined;
      const langInQuery = typeof query.lang === 'string' ? query.lang : undefined;
      const productId = productInQuery ?? flowStore.checkout.data.product_id?.toString();

      await router.replace({
        query: {
          ...(langInQuery ? { lang: langInQuery } : {}),
          ...(productId ? { product: productId } : {}),
          ro: 'true',
        },
      });
    }

    preloaded.value = true;
    flowStore.watchAndSaveChanges();

    await nextTick();

    if (askToResume.value) {
      resumeModal.value?.openModal();
    } else {
      openChangedModal();
    }
  } catch(error) {
    if (isAxiosError(error) && error.response && error.response.data) {
      errorMessage.value = error.response.data.message;
    } else {
      console.error(error);
    }
    setTimeout(() => {
      preloadingFailed.value = true;
    }, 2000);
  }
}

async function reload() {
  await doPreload();
}

async function reinitialize(restartWith?: CheckoutOptions) {
  flowStore.resetFlow();
  await doPreload(restartWith);
}

async function resumeReservation() {
  askToResume.value = false;
  restartOptions.value = undefined;

  // The resume question showed all of this, so nothing follows it.
  priceNotice.value = '';
  unavailableReasons.value = [];

  await router.replace({ query: withoutReservationParams(route.query) });
}

async function restartAfterChange() {
  const product = flowStore.checkout.data?.product_id;

  unavailableReasons.value = [];
  priceNotice.value = '';

  await reinitialize({ product } as CheckoutOptions);
}

function openChangedModal() {
  if (changeReasons.value.length === 0) return;

  changedModal.value?.openModal();
}

async function restartReservation() {
  askToResume.value = false;
  unavailableReasons.value = [];
  priceNotice.value = '';

  const options = restartOptions.value;
  restartOptions.value = undefined;

  await reinitialize(options);
}

onBeforeMount(async () => {
  await doPreload();
});
</script>
